/**
 * Адаптер «интерфейс мок-API прототипа (socrat-api.js) → наш API v3» для перенесённой оболочки.
 *
 * Прототип говорит snake_case и своими типами материалов: note ↔ doc, «доска» = mindmap со style.kind = 'board'
 * ↔ board, у задачи один assignee ↔ assignees[]. Здесь — только перевод форматов; права, проверки и связи
 * считает сервер.
 */

export interface ApiError extends Error {
  status: number;
  code?: string;
  fields?: unknown;
  retryAfter?: string | null;
}

const HEADERS = { 'Content-Type': 'application/json', 'X-Socrat-Request': '1' };

export async function http<T = any>(path: string, method = 'GET', body?: unknown): Promise<T> {
  let res: Response;
  try {
    res = await fetch(`/api${path}`, {
      method,
      credentials: 'same-origin',
      headers: HEADERS,
      ...(body === undefined ? {} : { body: JSON.stringify(body) }),
    });
  } catch {
    throw Object.assign(new Error('Нет соединения с сервером'), { status: 0 }) as ApiError;
  }
  const data = res.status === 204 ? {} : await res.json().catch(() => ({}));
  if (!res.ok) {
    throw Object.assign(new Error(data.error || `Ошибка ${res.status}`), {
      status: res.status,
      code: data.code,
      fields: data.fields,
      retryAfter: res.headers.get('Retry-After'),
    }) as ApiError;
  }
  return data as T;
}

const notYet = (what: string): Promise<never> =>
  Promise.reject(
    Object.assign(new Error(`${what} появятся в следующих версиях`), { status: 501, code: 'not_implemented' }),
  );

// ---------- форматы ----------

type Any = Record<string, any>;

export const toSpace = (s: Any) => ({
  id: s.id,
  name: s.name,
  slug: s.slug,
  description: s.description,
  color: s.color,
  logo: s.logoUrl,
  owner_id: s.ownerId,
  is_home: s.isHome,
  status: s.status,
  role: s.role,
  manage_private: s.managePrivate,
  plan_code: s.planCode,
  subscription_status: s.subscriptionStatus,
});

const toProject = (p: Any) => ({
  id: p.id,
  space_id: p.spaceId,
  type: p.type,
  name: p.name,
  description: p.description,
  icon: p.icon,
  color: p.color,
  template: p.template,
  visibility: p.visibility,
  ai_enabled: p.aiEnabled,
  permission: p.permission,
});

/** Содержимое с сервера → вид прототипа. */
export function contentIn(kind: string, c: Any = {}) {
  if (kind === 'doc') {
    // Сервер добавляет blocks: [] по умолчанию; прототип при наличии blocks игнорирует Markdown в text.
    const { blocks, schemaVersion: _v, ...rest } = c;
    return Array.isArray(blocks) && blocks.length ? { ...rest, blocks } : rest;
  }
  if (kind === 'board') return { ...c, style: { ...(c.style || {}), kind: 'board' } };
  if (kind === 'task') {
    const assignees = Array.isArray(c.assignees) ? c.assignees : [];
    return { ...c, assignees, assignee: assignees[0] ?? '' };
  }
  return c;
}

/** Содержимое прототипа → наш формат. */
export function contentOut(kind: string, c: Any | undefined) {
  if (!c) return c;
  if (kind === 'task') {
    // В дизайне один исполнитель (выбор из списка). Не меняли его — сохраняем всех (несколько бывает у задач
    // из чек-листа и API); выбрали другого или «Не назначен» — исполнитель один.
    const { assignee = '', ...rest } = c;
    const all: string[] = Array.isArray(rest.assignees) ? rest.assignees : [];
    return { ...rest, assignees: assignee === (all[0] ?? '') ? all : assignee ? [assignee] : [] };
  }
  return c;
}

export const kindIn = (k: string) => (k === 'doc' ? 'note' : k === 'board' ? 'mindmap' : k);
export const kindOut = (k: string, content?: Any) =>
  k === 'note' ? 'doc' : k === 'mindmap' && content?.style?.kind === 'board' ? 'board' : k;

export const toResource = (r: Any) => ({
  id: r.id,
  project_id: r.projectId,
  parent_id: r.parentId,
  kind: kindIn(r.kind),
  title: r.title,
  owner_id: r.ownerId,
  personal: r.personal,
  content: contentIn(r.kind, r.content),
  position: r.position,
  version: r.version,
  excerpt: r.excerpt,
  created_at: r.createdAt,
  updated_at: r.updatedAt,
  permission: r.permission,
  _kind: r.kind,
});

const toMetric = (m: Any) => ({
  id: m.id,
  project_id: m.projectId,
  name: m.name,
  unit: m.unit,
  target: m.target,
  actual: m.actual,
  period: m.period,
  version: m.version,
});

// ---------- API ----------

export function createLiveApi() {
  /** Тип материала по id — для правки задач (assignee ↔ assignees) без лишнего запроса. */
  const kinds = new Map<string, string>();
  const remember = (r: Any) => {
    kinds.set(r.id, r.kind);
    return r;
  };
  const spacesCache = new Map<string, Any>();

  const api = {
    // — спейсы и проекты —
    async getSpaces() {
      const list = await http<Any[]>('/spaces');
      list.forEach((s) => spacesCache.set(s.id, s));
      return list.map(toSpace);
    },
    async getProjects(sid: string) {
      return (await http<Any[]>(`/spaces/${sid}/projects`)).map(toProject);
    },
    async createSpace(b: { name: string; color?: string }) {
      const s = await http<Any>('/spaces', 'POST', {
        name: (b.name || 'Новый спейс').slice(0, 160),
        ...(b.color ? { color: b.color } : {}),
      });
      spacesCache.set(s.id, s);
      return { id: s.id };
    },
    async patchSpace(sid: string, b: Any) {
      return toSpace(await http(`/spaces/${sid}`, 'PATCH', b));
    },
    async deleteSpace(sid: string) {
      const name = spacesCache.get(sid)?.name ?? (await http<Any>(`/spaces/${sid}`)).name;
      return http(`/spaces/${sid}`, 'DELETE', { confirmName: name });
    },
    async createProject(sid: string, b: Any) {
      if (b.type === 'site') return notYet('Сайты');
      if (b.type === 'dept') return notYet('Отделы');
      const body: Any = {
        name: b.name,
        description: b.description || '',
        type: 'project',
        visibility: b.visibility || 'space',
      };
      if (b.icon && /^(lucide|tabler|ph):[a-z0-9-]+$/.test(b.icon)) body.icon = b.icon;
      if (b.color && /^#[0-9a-fA-F]{6}$/.test(b.color)) body.color = b.color;
      if (b.template) body.template = String(b.template).slice(0, 40);
      const p = await http<Any>(`/spaces/${sid}/projects`, 'POST', body);
      return { id: p.id };
    },
    async patchProject(pid: string, b: Any) {
      const allowed: Any = {};
      for (const k of ['name', 'description', 'icon', 'color']) if (b[k] !== undefined) allowed[k] = b[k];
      if (b.ai_enabled !== undefined) allowed.aiEnabled = b.ai_enabled;
      return toProject(await http(`/projects/${pid}`, 'PATCH', allowed));
    },

    // — снимок проекта (с содержимым: превью на главной и редакторы) —
    async getProject(pid: string) {
      const s = await http<Any>(`/projects/${pid}?include=content`);
      const resources = s.resources.map((r: Any) => remember(toResource(r)));
      s.resources.forEach((r: Any) => kinds.set(r.id, r.kind));
      return {
        project: toProject(s.project),
        resources,
        links: s.links.map((l: Any) => ({
          id: l.id,
          source: l.sourceId,
          target: l.targetId,
          kind: l.kind,
          score: l.score,
          state: l.state,
          reason: l.reason ?? null, // объяснение AI — в дизайне для него нет места (ADR-015)
        })),
        metrics: s.metrics.map(toMetric),
        aiEnabled: !!s.ai,
      };
    },

    // — материалы —
    async createResource(pid: string, b: Any) {
      const kind = kindOut(b.kind, b.content);
      const r = await http<Any>(`/projects/${pid}/resources`, 'POST', {
        kind,
        title: (b.title || 'Без названия').slice(0, 160),
        parentId: b.parent_id ?? null,
        personal: !!b.personal,
        ...(b.content !== undefined ? { content: contentOut(kind, b.content) } : {}),
        ...(b.position ? { position: b.position } : {}),
      });
      kinds.set(r.id, r.kind);
      return toResource(r);
    },
    async patchResource(id: string, b: Any) {
      // Только положение — отдельным запросом без версии (не конфликтует с правкой содержимого).
      if (b.position && b.content === undefined && b.title === undefined) {
        await http(`/resources/${id}/position`, 'PUT', b.position);
        return toResource(await http(`/resources/${id}`));
      }
      const kind = kinds.get(id) ?? (await http<Any>(`/resources/${id}`)).kind;
      const r = await http<Any>(`/resources/${id}`, 'PATCH', {
        version: b.version,
        ...(b.title !== undefined ? { title: b.title } : {}),
        ...(b.content !== undefined ? { content: contentOut(kind, b.content) } : {}),
        ...(b.personal !== undefined ? { personal: b.personal, confirmAccessChange: true } : {}),
      });
      return remember(toResource(r));
    },
    async moveResource(id: string, b: Any) {
      const version = b.version ?? (await http<Any>(`/resources/${id}`)).version;
      await http(`/resources/${id}/move`, 'POST', { version, parentId: b.parent_id ?? null });
      return { ok: true };
    },
    async deleteResource(id: string) {
      const { version } = await http<Any>(`/resources/${id}`);
      return http(`/resources/${id}`, 'DELETE', { version });
    },

    async renameResource(id: string, version: number, title: string) {
      return remember(toResource(await http(`/resources/${id}`, 'PATCH', { version, title })));
    },
    duplicate: (id: string) => http<Any>(`/resources/${id}/duplicate`, 'POST').then(toResource),
    restore: (id: string) => http<Any>(`/resources/${id}/restore`, 'POST').then(toResource),
    trash: (pid: string) => http<Any[]>(`/projects/${pid}/trash`),
    getAccess: (id: string) => http<Any[]>(`/resources/${id}/access`),
    putAccess: (id: string, version: number, grants: Any[]) =>
      http<Any[]>(`/resources/${id}/access`, 'PUT', { version, grants }),
    audience: (id: string, personal: boolean) => http<Any[]>(`/resources/${id}/audience?personal=${personal}`),
    async setPersonal(id: string, version: number, personal: boolean) {
      return toResource(await http(`/resources/${id}`, 'PATCH', { version, personal, confirmAccessChange: true }));
    },
    getResource: (id: string) => http<Any>(`/resources/${id}`).then(toResource),
    // версии совместного редактирования (ADR-011)
    versions: (id: string) => http<Any[]>(`/resources/${id}/versions`),
    version: (id: string, vid: string) => http<Any>(`/resources/${id}/versions/${vid}`),

    // — обсуждения и «Входящие» (ADR-012) —
    projectComments: (pid: string) => http<Any[]>(`/projects/${pid}/comments`),
    addComment: (rid: string, b: { body: string; parentId?: string | null; mentions?: string[] }) =>
      http<Any>(`/resources/${rid}/comments`, 'POST', b),
    patchComment: (id: string, b: Any) => http<Any>(`/comments/${id}`, 'PATCH', b),
    deleteComment: (id: string) => http(`/comments/${id}`, 'DELETE'),
    notifications: (filter = 'all') => http<Any>(`/notifications?filter=${filter}&limit=50`),
    /** Поиск по всем спейсам (ADR-014): `{ items: [{ id, kind, title, projectId, projectName, spaceName }] }`. */
    search: (q: string, limit = 12) => http<Any>(`/search?q=${encodeURIComponent(q)}&limit=${limit}`),
    readNotifications: (b: { ids: string[] } | { all: true }) => http<Any>('/notifications/read', 'POST', b),

    // — KPI —
    async createMetric(pid: string, b: Any) {
      const m = await http<Any>(`/projects/${pid}/metrics`, 'POST', b);
      return { id: m.id };
    },
    async patchMetric(id: string, b: Any) {
      return toMetric(await http(`/metrics/${id}`, 'PATCH', b));
    },

    // — ещё нет на сервере —
    // — агенты и MCP (ADR-016) —
    tokens: () => http<Any[]>('/me/tokens'),
    createToken: (b: { name?: string; scopes: string[] }) => http<Any>('/me/tokens', 'POST', b),
    patchToken: (id: string, b: { scopes?: string[] }) => http<Any>(`/me/tokens/${id}`, 'PATCH', b),
    revokeToken: (id: string) => http<Any>(`/me/tokens/${id}`, 'DELETE'),
    mcpServers: () => http<Any[]>('/me/mcp-servers'),
    createMcpServer: (b: { name: string; url: string; transport: string; token?: string }) =>
      http<Any>('/me/mcp-servers', 'POST', b),
    patchMcpServer: (id: string, b: { enabled?: boolean }) => http<Any>(`/me/mcp-servers/${id}`, 'PATCH', b),
    deleteMcpServer: (id: string) => http<Any>(`/me/mcp-servers/${id}`, 'DELETE'),

    /** AI-связи (ADR-015): 202 и id задания, итог приходит событием `analyze` (LiveController.runAi). */
    analyze: (pid: string) => http<Any>(`/projects/${pid}/analyze`, 'POST'),
    patchLink: (id: string, state: 'accepted' | 'dismissed') => http<Any>(`/links/${id}`, 'PATCH', { state }),
    getSiteData: () => notYet('Сайты'),
    putSiteData: () => notYet('Сайты'),
    getDeptData: () => notYet('Отделы'),
    putDeptData: () => notYet('Отделы'),

    // — аккаунт (используется LiveController) —
    me: () => http<Any>('/me'),
    patchMe: (b: Any) => http<Any>('/me', 'PATCH', b),
    config: () => http<Any>('/config'),
    // white list (ADR-017): только администратор через VPN, иначе 404
    adminAccess: (status: string) => http<Any>(`/admin/access?status=${status}`),
    adminDecide: (id: string, decision: 'approve' | 'reject') => http<Any>(`/admin/access/${id}`, 'POST', { decision }),
    adminAllowlist: () => http<Any[]>('/admin/allowlist'),
    adminAllow: (email: string) => http<Any>('/admin/allowlist', 'POST', { email }),
    adminDisallow: (email: string) => http<Any>(`/admin/allowlist/${encodeURIComponent(email)}`, 'DELETE'),
    login: (email: string, password: string) => http<Any>('/auth/login', 'POST', { email, password }),
    register: (b: Any) => http<Any>('/auth/register', 'POST', b),
    logout: () => http('/auth/logout', 'POST'),
    logoutAll: () => http('/auth/logout-all', 'POST'),
    resetRequest: (email: string) => http('/auth/password/reset-request', 'POST', { email }),
    invite: (sid: string, b: Any) => http<Any>(`/spaces/${sid}/invitations`, 'POST', b),
    invitations: (sid: string) => http<Any[]>(`/spaces/${sid}/invitations`),
    revokeInvitation: (sid: string, id: string) => http(`/spaces/${sid}/invitations/${id}`, 'DELETE'),
    members: (sid: string) => http<Any[]>(`/spaces/${sid}/members`),
    updateMember: (sid: string, uid: string, b: Any) => http(`/spaces/${sid}/members/${uid}`, 'PATCH', b),
    removeMember: (sid: string, uid: string) => http(`/spaces/${sid}/members/${uid}`, 'DELETE'),
    leaveSpace: (sid: string) => http(`/spaces/${sid}/leave`, 'POST'),
    sessions: () => http<Any[]>('/auth/sessions'),
    revokeSession: (id: string) => http(`/auth/sessions/${id}`, 'DELETE'),
    deleteMe: (confirmEmail: string) => http('/me', 'DELETE', { confirmEmail }),
  };
  return api;
}

export type LiveApi = ReturnType<typeof createLiveApi>;
